import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useTrips } from "@/api/trips";
import { type Trip, TripStatus } from "@/api.types.v2";
import { TripProgress } from "./trip-progress";
import { ViewTripButton } from "./view-trip-button";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { getOperatorDisplayName } from "@/lib/safe-access";

const statusColors: Record<TripStatus, string> = {
  [TripStatus.PLANNED]: "bg-blue-500",
  [TripStatus.ACTIVE]: "bg-yellow-500",
  [TripStatus.COMPLETED]: "bg-green-500",
  [TripStatus.ARCHIVED]: "bg-gray-500",
};

const TRIP_STATUSES = {
  PLANNED: "Planned",
  ACTIVE: "Active",
  COMPLETED: "Completed",
  ARCHIVED: "Archived",
} as const;

export function TripsTable() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState<Trip["status"] | null>(null);
  const { carrierIds, dispatchCenterIds } = useCarrierScope();

  const { data: tripsResponse } = useTrips({
    page,
    limit,
    status: statusFilter ?? undefined,
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4">
        <Select
          value={statusFilter ?? "ALL"}
          onValueChange={(value) =>
            setStatusFilter(value === "ALL" ? null : (value as Trip["status"]))
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            {Object.entries(TRIP_STATUSES).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Trip Number</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Time Spent</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Dispatch center</TableHead>
              <TableHead>Tasks</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tripsResponse?.data.map((trip) => (
              <TableRow key={trip.id}>
                <TableCell>
                  <div>
                    <p className="text-md font-medium">#{trip.tripNumber}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${statusColors[trip.status]}`}
                    variant="default"
                  >
                    {trip.status.replace(/_/g, " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <div>
                      {trip.startTime && (
                        <p className="text-sm text-gray-500">
                          Started: {new Date(trip.startTime).toLocaleString()}
                        </p>
                      )}
                      {trip.endTime && (
                        <p className="text-sm text-gray-500">
                          Ended: {new Date(trip.endTime).toLocaleString()}
                        </p>
                      )}
                      {typeof trip.actualDuration === "number" && (
                        <p className="font-medium">
                          Duration: {trip.actualDuration} mins
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {trip.operator
                        ? getOperatorDisplayName(trip.operator)
                        : "No operator assigned"}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {trip.vehicle === null
                        ? "No vehicle assigned"
                        : trip.vehicle?.licensePlate ||
                          trip.vehicle?.code ||
                          "Vehicle unavailable"}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {trip.dispatchCenter?.name ??
                        "Dispatch center unavailable"}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <TripProgress progress={trip} />
                    <p className="text-sm text-gray-500">
                      {trip.waypoints?.length ?? 0} stops
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <ViewTripButton tripId={trip.id} size="sm" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-gray-500">
          Showing {(page - 1) * limit + 1} to{" "}
          {Math.min(page * limit, tripsResponse?.metadata.total ?? 0)} of{" "}
          {tripsResponse?.metadata.total ?? 0} results
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= (tripsResponse?.metadata.totalPages ?? 0)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
